import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { INSURANCES } from "@/data/heallexa";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/insurance/")({
 head: () => {
 const title = "Browse Doctors by Insurance | Heallexa";
 const description =
 "Find in-network doctors and specialists that accept your insurance. Heallexa covers Aetna, Blue Cross Blue Shield, Cigna, UnitedHealthcare, Medicare, Medicaid, and more — searchable across 6.3 million verified U.S. providers.";
 const url = `${SITE}/insurance`;
 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 { name: "twitter:title", content: title },
 { name: "twitter:description", content: description },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: title,
 description,
 url,
 isPartOf: { "@id": `${SITE}/#website` },
 about: { "@type": "Thing", name: "Health Insurance Plans" },
 breadcrumb: {
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "Insurance", item: url },
 ],
 },
 }),
 },
 ],
 };
 },
 component: InsuranceIndex,
});

function InsuranceIndex() {
 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <Reveal from="up">
 <header className="mb-10">
 <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Heallexa Directory</p>
 <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
 Browse Doctors by Insurance
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[640px] leading-relaxed">
 Find in-network providers that accept your plan across{" "}
 <strong className="text-foreground font-medium">{INSURANCES.length} major U.S. insurance carriers</strong>{" "}
 and government programs. Heallexa covers Aetna, Blue Cross Blue Shield, Cigna,
 UnitedHealthcare, Humana, Kaiser, Medicare, Medicaid, and more — all searchable
 across 6.3 million verified providers. Select your plan below to get started.
 </p>
 </header>
 </Reveal>

 <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
 {INSURANCES.map((i, idx) => (
 <Reveal key={i.id} as="li" from="up" delay={Math.min(idx, 8) * 70}>
 <Link
 to="/insurance/$slug"
 params={{ slug: i.id }}
 className="group hover-lift cursor-pointer card-surface p-6 flex flex-col h-full rounded-md"
 >
 <div className="flex items-start gap-3">
 <span
 className="h-11 w-11 rounded-md inline-flex items-center justify-center text-[13px] font-semibold text-white shrink-0"
 style={{ backgroundColor: i.color }}
 aria-hidden
 >
 {i.short.slice(0, 2).toUpperCase()}
 </span>
 <div className="min-w-0 flex-1">
 <h2 className="text-[16px] font-semibold text-foreground leading-tight">{i.name}</h2>
 <p className="mt-1 text-[13.5px] text-muted-foreground">{i.short}</p>
 </div>
 </div>
 <div className="mt-auto pt-4 border-t border-border/60 flex items-center justify-end text-[12.5px]">
 <span className="inline-flex items-center gap-1 font-medium text-primary group-hover:gap-2 transition-all">
 Browse
 <ChevronRight className="h-3.5 w-3.5" />
 </span>
 </div>
 </Link>
 </Reveal>
 ))}
 </ul>
 </main>
 <FooterSection />
 </div>
 );
}
